import React, { useCallback, useMemo } from "react";

import Select from "antd/es/select";
import cn from "classnames";
import { useIntl } from "react-intl";
import invariant from "tiny-invariant";

import { EntityType } from "@mapmycustomers/shared/enum";
import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import IFiltersConfig from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import FilterModel, {
  FilterCondition,
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import getFieldColumnOptions, {
  getOptions,
} from "@app/component/filtering/utils/getFieldColumnOptions";
import useFieldsUsage from "@app/component/filtering/utils/useFieldsUsage";
import FilterOperatorAction from "@app/types/FilterOperatorAction";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { isCombinedCondition } from "@app/util/viewModel/assert";

import commonStyles from "./CommonStyles.module.scss";
import styles from "./FieldColumn.module.scss";

const yes = () => true;

interface Props {
  checkInFieldsVisibility: boolean;
  className?: string;
  context: string; // used to handle field usage
  disabled?: boolean;
  entityType: "universal" | EntityType;
  field?: IField;
  fieldModel: IFieldModel;
  filterModel: Partial<FilterModel>;
  filterOperators: FilterOperatorAction;
  firstFilter?: boolean;
  focused?: boolean;
  hiddenFields?: IField[];
  onChange?: (
    oldField: IField | undefined,
    newField: IField,
    newCondition: SimpleCondition
  ) => void;
  shouldShowField?: (field: IField) => boolean; // whether to include or not this field
  showColumnGroups?: boolean;
}

const FieldColumn: React.FC<Props> = ({
  checkInFieldsVisibility,
  className,
  context,
  disabled,
  entityType,
  field,
  fieldModel,
  filterModel,
  filterOperators,
  firstFilter,
  focused,
  hiddenFields,
  onChange,
  shouldShowField = yes,
  showColumnGroups = true,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const filterCondition: FilterCondition | undefined = field
    ? filterModel[field.filterName]
    : undefined;
  invariant(
    !filterCondition || (filterCondition && !isCombinedCondition(filterCondition)),
    `Combined conditions are not yet supported in filter bar`
  );

  const categorizedFields = useMemo(() => {
    const fieldNamesAlreadyUsedInFilterModel = new Set(
      Object.keys(filterModel)
        // If condition is "undefined" we assume, field is not used in filters
        .filter((filterName) => isDefined(filterModel[filterName]))
    );
    const hiddenFieldsNames = new Set((hiddenFields ?? []).map(({ name }) => name));
    const categorizedFields = fieldModel.categorizedFields;
    // remove fields that were already used in filters, we don't allow filtering by one field twice
    Object.keys(categorizedFields).forEach((category) => {
      categorizedFields[category as FieldCategory] = categorizedFields[category as FieldCategory]
        // only include those fields which are filterable and not yet used in filters + current field itself
        // but always skip fields which are marked as hidden
        .filter((candidateField) => {
          const { filter, filterName, hasFeature, name } = candidateField;
          return (
            !hiddenFieldsNames.has(name) &&
            candidateField.isReadable &&
            filter !== false &&
            (field?.filterName === filterName ||
              !fieldNamesAlreadyUsedInFilterModel.has(filterName)) &&
            (!hasFeature(FieldFeature.CHECK_IN) || checkInFieldsVisibility) &&
            shouldShowField(candidateField)
          );
        });
    });
    return categorizedFields;
  }, [checkInFieldsVisibility, field, fieldModel, filterModel, hiddenFields, shouldShowField]);

  const { markFieldAsUsed, recentlyUsedFields } = useFieldsUsage(context, entityType);

  const handleFieldChange = useCallback(
    (name: IField["name"]) => {
      const newField = fieldModel.getByName(name);
      analytics.using(["Select Field"], { field: newField?.displayName });
      if (newField && newField.filter) {
        const availableOperators = filterOperators(newField, newField.filter.operators);
        const newOperator =
          availableOperators.find(({ operator }) => operator === filterCondition?.operator) ??
          availableOperators.find(
            ({ operator }) => operator === (newField.filter as IFiltersConfig).defaultOperator
          ) ??
          availableOperators[0];

        if (newOperator) {
          const newInstance = newField.filter.filterInstances[newOperator.instance];

          // here's a special rule not included into "doesSupportValue" logic
          // we only transfer value when both fields have a matching filterType and only from a limited
          // list of types, otherwise old value is ignored.
          // This is done because date filters store their value as a string, and thus are technically
          // compatible with some other field types, such as STRING type field. But we don't want to show
          // user a date in ISO format in a text field.
          const canTransferValue = !field
            ? false
            : [
                FieldType.BOOLEAN,
                FieldType.DATE,
                FieldType.DATE_TIME,
                FieldType.NUMBER,
                FieldType.STRING,
              ].includes(field.filterType) && field.filterType === newField.filterType;

          const newValue =
            filterCondition && canTransferValue
              ? newInstance.instance.doesSupportValue?.(filterCondition.value, newOperator.operator)
                ? filterCondition.value
                : undefined
              : undefined;
          onChange?.(field, newField, { operator: newOperator.operator, value: newValue });
          markFieldAsUsed(newField);
        }
      }
    },
    [analytics, field, fieldModel, filterCondition, filterOperators, markFieldAsUsed, onChange]
  );

  return (
    <div className={cn(styles.container, className)}>
      {!firstFilter && (
        <span className={styles.conjunction}>
          {intl.formatMessage({
            id: "filters.defaultConjunction",
            defaultMessage: "AND",
            description:
              "Conjunction word to show in filters grid for all filters starting from the 2nd",
          })}
        </span>
      )}
      <Select<IField["name"]>
        className={cn(styles.fieldNameSelectBox, { [commonStyles.focusSplash]: focused })}
        disabled={disabled}
        dropdownMatchSelectWidth={false}
        onChange={handleFieldChange}
        optionFilterProp="label"
        placeholder={intl.formatMessage({
          id: "filters.fieldPlaceholder",
          defaultMessage: "Select field",
          description: "Placeholder displayed in a filter field.",
        })}
        showSearch
        value={field?.name}
      >
        {recentlyUsedFields.length > 0 && (
          <Select.OptGroup
            key="recent"
            label={intl.formatMessage({
              id: "filters.recentlyUsed",
              defaultMessage: "Recently Used",
              description: "Recently Used list of fields in filter dropdown",
            })}
          >
            {getOptions(recentlyUsedFields, filterModel, fieldModel, "recent")}
          </Select.OptGroup>
        )}
        {recentlyUsedFields.length > 0 && !showColumnGroups ? (
          <Select.OptGroup
            key="other"
            label={intl.formatMessage({
              id: "filters.otherFields",
              defaultMessage: "Other",
              description: "'Other' list of fields in filter dropdown",
            })}
          >
            {getFieldColumnOptions(
              intl,
              categorizedFields,
              filterModel,
              fieldModel,
              showColumnGroups
            )}
          </Select.OptGroup>
        ) : (
          getFieldColumnOptions(intl, categorizedFields, filterModel, fieldModel, showColumnGroups)
        )}
      </Select>
    </div>
  );
};

export default FieldColumn;
